import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import Pipeline from 'types/pipeline';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useUser } from 'hooks/useAuth';
import { updateWorkspaceBooleanTrait } from 'services/customer_tracking';

import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

export type UsePipelinesParams = {
  contains_name?: string;
  pipeline_ids?: Array<string>;
  pipeline_names?: Array<string>;
};

type UsePipelinesOptions = UseQueryOptions<
  OffsetPaginated<Pipeline>,
  APIError
> & {
  params?: UsePipelinesParams & OffsetPaginationParams;
};

export function usePipelines({
  queryKey = ['pipelines'],
  params,
  onSuccess,
  ...options
}: UsePipelinesOptions = {}) {
  const { applicationID, client } = useAPI();
  const user = useUser();

  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get<OffsetPaginated<Pipeline>>(
        `/apps/${applicationID}/pipelines`,
        {
          params: queryParams,
        }
      );

      const pipelines = data.data.map((pipeline) =>
        initializeAPIPipeline(
          new Pipeline(
            pipeline.id,
            pipeline.name,
            pipeline.application_id,
            pipeline.definition,
            pipeline.created_at,
            pipeline.updated_at
          )
        )
      );

      return { ...data, data: pipelines };
    },
    {
      ...options,
      onSuccess(data) {
        onSuccess?.(data);

        if (!user) {
          return;
        }

        const hasNonEmptyPipeline = data.data.some(
          (pipeline) => pipeline.definition.length > 0
        );

        updateWorkspaceBooleanTrait({
          userId: user.id,
          workspaceId: applicationID,
          trait: 'has_nonempty_pipeline',
          value: hasNonEmptyPipeline,
        });
      },
    }
  );
}

export function initializeAPIPipeline(pipeline: Pipeline) {
  // if (typeof pipeline.definition === 'string') {
  //   try {
  //     (pipeline as any).definition = JSON.parse(pipeline.definition);
  //   } catch {
  //     (pipeline as any).definition = [];
  //   }
  // }

  pipeline.updateDeployTimeEditableNodesFromDefinition();
  pipeline.updateNodeDescriptionsFromDefinition();

  return pipeline;
}
